import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import useHoverCard from '../../hook/useHoverCard';

type Tone = 'pt' | 'group';

export type ActivePassSummaryItem = {
    id?: string;
    name?: string;
    current?: number;
    total?: number;
    remaining?: number;
    coachName?: string;
    periodText?: string;
    expiresAt?: string;
    tone?: Tone;
};

type Props = {
    items: ActivePassSummaryItem[];
    tone?: Tone;
    maxVisible?: number;
    emptyText?: string;
};

const TONE_STYLE: Record<Tone, { bg: string; border: string; text: string; accent: string }> = {
    pt: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8', accent: '#2563EB' },
    group: { bg: '#F5F3FF', border: '#DDD6FE', text: '#6D28D9', accent: '#7C3AED' },
};

const TONE_LABEL: Record<Tone, string> = { pt: 'PT 수강권', group: '그룹 수강권' };

const EMPTY_STYLE: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 34,
    height: 24,
    padding: '0 10px',
    borderRadius: 999,
    backgroundColor: '#F8FAFC',
    color: '#CBD5E1',
    fontSize: 13,
    fontWeight: 700,
};

const CARD_WIDTH = 268;
const CARD_GAP = 8;
const VIEWPORT_MARGIN = 8;
// 카드 높이 상한을 두기 위한 항목 수 제한. 넘치는 만큼은 스크롤 대신 안내 문구로 알린다
// (카드가 pointerEvents:'none' 이라 스크롤 영역을 만들어도 사용자가 닿을 수 없다).
const MAX_CARD_ITEMS = 4;

const formatSession = (value?: number) => (typeof value === 'number' && Number.isFinite(value) ? value : '-');

const getRemainingTone = (remaining?: number) => {
    if (typeof remaining !== 'number') return { bg: '#F8FAFC', color: '#64748B', border: '#E2E8F0' };
    if (remaining <= 2) return { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' };
    if (remaining <= 5) return { bg: '#FFFBEB', color: '#B45309', border: '#FDE68A' };
    return { bg: '#ECFDF5', color: '#047857', border: '#A7F3D0' };
};

const truncateName = (name?: string) => {
    const text = name?.trim() || '수강권';
    return text.length > 12 ? `${text.slice(0, 12)}...` : text;
};

// 만료까지 남은 일수. 이미 지났으면 음수. 시각이 아니라 날짜 경계로 센다
// (양쪽 다 startOf('day') — 한쪽만 endOf 를 쓰면 과거 날짜가 하루 적게 나온다).
const getDaysLeft = (expiresAt?: string) => {
    if (!expiresAt) return null;
    const target = dayjs(expiresAt);
    if (!target.isValid()) return null;
    return target.startOf('day').diff(dayjs().startOf('day'), 'day');
};

const DDayText = ({ expiresAt }: { expiresAt?: string }) => {
    const daysLeft = getDaysLeft(expiresAt);
    if (daysLeft === null) return null;

    const { label, color } =
        daysLeft < 0
            ? { label: `${Math.abs(daysLeft)}일 지남`, color: '#F87171' }
            : daysLeft === 0
              ? { label: '오늘 만료', color: '#FBBF24' }
              : { label: `D-${daysLeft}`, color: daysLeft <= 7 ? '#FBBF24' : '#94A3B8' };

    return <span style={{ color, fontWeight: daysLeft <= 7 ? 700 : 500 }}>{label}</span>;
};

/**
 * 칩 위에 뜨는 상세 카드.
 * Grid 의 스크롤 컨테이너(overflowX: auto → overflow-y 도 auto 로 계산됨)가 위쪽으로 넘치는 요소를
 * 잘라내기 때문에, 셀 안에 absolute 로 띄우면 상단 행에서 잘린다. 그래서 body 로 portal 하고
 * viewport 좌표(fixed)로 직접 배치한다.
 */
const PassDetailCard = ({ items, tone, rect, title }: { items: ActivePassSummaryItem[]; tone: Tone; rect: DOMRect; title: string }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const [cardHeight, setCardHeight] = useState(0);

    const shownItems = items.slice(0, MAX_CARD_ITEMS);
    const overflowCount = items.length - shownItems.length;

    // 실제 높이를 재서 배치한다. 항목이 많은 +N 카드는 위로 띄우면 화면 밖으로 나갈 수 있다.
    // useLayoutEffect 라 paint 전에 위치가 확정된다.
    useLayoutEffect(() => {
        if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
    }, [items]);

    const half = CARD_WIDTH / 2;
    const left = Math.min(Math.max(rect.left + rect.width / 2, half + VIEWPORT_MARGIN), window.innerWidth - half - VIEWPORT_MARGIN);
    // 기본은 칩 위. 위에 안 들어가면 더 넓은 쪽으로 뒤집고, 그래도 넘치면 뷰포트 안으로 당긴다.
    // portal + fixed 라 표의 스크롤 영역과 무관하다.
    const spaceAbove = rect.top - CARD_GAP - VIEWPORT_MARGIN;
    const spaceBelow = window.innerHeight - rect.bottom - CARD_GAP - VIEWPORT_MARGIN;
    const flipDown = cardHeight > spaceAbove && spaceBelow > spaceAbove;
    const rawTop = flipDown ? rect.bottom + CARD_GAP : rect.top - CARD_GAP - cardHeight;
    const top = Math.max(VIEWPORT_MARGIN, Math.min(rawTop, window.innerHeight - cardHeight - VIEWPORT_MARGIN));

    return createPortal(
        <div
            ref={cardRef}
            style={{
                position: 'fixed',
                left,
                top,
                transform: 'translateX(-50%)',
                width: CARD_WIDTH,
                zIndex: 9999,
                padding: 12,
                borderRadius: 10,
                backgroundColor: 'rgba(15,23,42,0.98)',
                boxShadow: '0 14px 34px rgba(15,23,42,0.24)',
                color: '#E2E8F0',
                textAlign: 'left',
                pointerEvents: 'none',
            }}
        >
            <div style={{ fontSize: 12, fontWeight: 800, color: '#F8FAFC', marginBottom: 8 }}>{title}</div>
            {shownItems.map((item, index) => {
                const remainTone = getRemainingTone(item.remaining);
                const itemToneStyle = TONE_STYLE[item.tone ?? tone];
                return (
                    <div key={`${item.id || item.name || index}-${index}`} style={{ padding: '8px 0', borderTop: index > 0 ? '1px solid #334155' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{ fontSize: 12, fontWeight: 800, color: '#F8FAFC' }}>{item.name || '수강권'}</span>
                            <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 800, color: itemToneStyle.accent }}>
                                {formatSession(item.current)}/{formatSession(item.total)}회
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, fontSize: 11 }}>
                            <span style={{ color: remainTone.color, fontWeight: 800 }}>잔여 {formatSession(item.remaining)}회</span>
                            {item.coachName && <span style={{ color: '#94A3B8' }}>· {item.coachName}</span>}
                        </div>
                        {(item.periodText || item.expiresAt) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 11 }}>
                                {item.periodText && <span style={{ color: '#94A3B8' }}>{item.periodText}</span>}
                                <DDayText expiresAt={item.expiresAt} />
                            </div>
                        )}
                    </div>
                );
            })}
            {overflowCount > 0 && (
                <div style={{ paddingTop: 8, borderTop: '1px solid #334155', fontSize: 11, color: '#94A3B8' }}>
                    외 {overflowCount}개는 회원 프로필에서 확인할 수 있어요
                </div>
            )}
        </div>,
        document.body
    );
};

/** 트리거를 감싸고 hover 시 상세 카드를 띄운다. 표시/해제 규칙은 useHoverCard 참고. */
const PassHoverTarget = ({ items, tone, title, children }: { items: ActivePassSummaryItem[]; tone: Tone; title: string; children: React.ReactNode }) => {
    const { anchorRef, rect, anchorProps } = useHoverCard<HTMLDivElement>();

    return (
        <div
            ref={anchorRef}
            style={{ display: 'inline-flex', flexShrink: 0, cursor: 'help', maxWidth: '100%' }}
            {...anchorProps}
        >
            {children}
            {rect && items.length > 0 && <PassDetailCard items={items} tone={tone} rect={rect} title={title} />}
        </div>
    );
};

const ActivePassSummary = ({ items, tone = 'pt', maxVisible = 2, emptyText = '—' }: Props) => {
    const activeItems = items.filter(Boolean);
    const visibleItems = activeItems.slice(0, maxVisible);
    const hiddenItems = activeItems.slice(maxVisible);

    if (activeItems.length === 0) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <span style={EMPTY_STYLE}>{emptyText}</span>
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                flexWrap: 'nowrap',
                maxWidth: '100%',
                padding: '2px 0',
            }}
        >
            {visibleItems.map((item, index) => {
                const remainTone = getRemainingTone(item.remaining);
                const itemToneStyle = TONE_STYLE[item.tone ?? tone];
                return (
                    <PassHoverTarget
                        key={`${item.id || item.name || index}-${index}`}
                        items={[item]}
                        tone={tone}
                        title={TONE_LABEL[item.tone ?? tone]}
                    >
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr) auto',
                                alignItems: 'center',
                                columnGap: 5,
                                minWidth: 122,
                                maxWidth: 150,
                                height: 34,
                                padding: '4px 7px',
                                borderRadius: 8,
                                backgroundColor: itemToneStyle.bg,
                                border: `1px solid ${itemToneStyle.border}`,
                                boxSizing: 'border-box',
                                flexShrink: 0,
                            }}
                        >
                            <div style={{ minWidth: 0 }}>
                                <div
                                    style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: 11,
                                        lineHeight: '13px',
                                        fontWeight: 800,
                                        color: itemToneStyle.text,
                                    }}
                                >
                                    {truncateName(item.name)}
                                </div>
                                <div style={{ fontSize: 11, lineHeight: '13px', fontWeight: 800, color: '#0F172A' }}>
                                    {formatSession(item.current)}/{formatSession(item.total)}회
                                </div>
                            </div>
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 36,
                                    height: 20,
                                    padding: '0 6px',
                                    borderRadius: 999,
                                    backgroundColor: remainTone.bg,
                                    border: `1px solid ${remainTone.border}`,
                                    color: remainTone.color,
                                    fontSize: 11,
                                    fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatSession(item.remaining)}회
                            </span>
                        </div>
                    </PassHoverTarget>
                );
            })}
            {hiddenItems.length > 0 && (
                <PassHoverTarget items={hiddenItems} tone={tone} title={`숨겨진 활성 수강권 ${hiddenItems.length}개`}>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: 24,
                            padding: '0 8px',
                            borderRadius: 999,
                            backgroundColor: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            color: '#475569',
                            fontSize: 11,
                            fontWeight: 800,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        +{hiddenItems.length}
                    </span>
                </PassHoverTarget>
            )}
        </div>
    );
};

export default ActivePassSummary;
